import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import apiClient from '@/utils/api';
import { getAuthToken } from '@/utils/auth';
import { saveDraft, getDraft, clearDraft, setPendingAction } from '@/utils/draftManager';
import { toast } from 'sonner';
import {
  Building2, MapPin, IndianRupee, CheckCircle,
  Rocket, Star, Crown, ArrowRight, ArrowLeft,
  TrendingUp, Target, Eye, Camera,
  Upload, X, Image as ImageIcon, FileText, Save
} from 'lucide-react';
import { useAddonPricing } from '@/hooks/useAddonPricing';
import LocationSearch from '@/components/LocationSearch';
import AreaInput from '@/components/AreaInput';
import {
  trackListingFormStarted,
  trackListingStepCompleted,
  trackListingSubmitted,
  trackListingAbandoned,
} from '@/utils/analytics';
import TurnstileWidget from '@/components/common/TurnstileWidget';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ALLOWED_DOC_TYPES = ['application/pdf'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_DOC_SIZE = 10 * 1024 * 1024;
const MAX_IMAGES = 10;
const MAX_FLOOR_PLANS = 3;
const MAX_BROCHURES = 2;

const propertySubtypes = {
  residential: [
    { value: 'apartment', label: 'Apartment' },
    { value: 'villa', label: 'Villa / House' },
    { value: 'plot', label: 'Plot / Land' },
    { value: 'penthouse', label: 'Penthouse' },
    { value: 'builder_floor', label: 'Builder Floor' },
  ],
  commercial: [
    { value: 'office', label: 'Office Space' },
    { value: 'shop', label: 'Shop / Showroom' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'co_working', label: 'Co-working Space' },
  ],
  agricultural: [
    { value: 'farmland', label: 'Farm Land' },
    { value: 'farmhouse', label: 'Farm House' },
    { value: 'agricultural_plot', label: 'Agricultural Plot' },
  ]
};

const DEFAULT_BOOST_TIERS = {
  standard: { name: "Standard Boost", price: 2999, duration: "7 days", multiplier: "1.5x", features: ["1.5x visibility in search results", "Featured in category listings", "Priority in nearby searches", "Basic analytics dashboard"] },
  premium: { name: "Premium Boost", price: 5999, duration: "14 days", multiplier: "2.5x", popular: true, features: ["2.5x visibility in search results", "Featured on homepage carousel", "Priority in all location searches", "Social media promotion", "Advanced analytics dashboard", "Dedicated support"] },
  elite: { name: "Elite Boost", price: 9999, duration: "30 days", multiplier: "4x", features: ["4x visibility in search results", "Premium homepage placement", "Targeted email campaigns", "WhatsApp broadcast to matched buyers", "Professional photo editing", "Virtual tour creation", "Full analytics suite", "Priority 24/7 support"] }
};

const BOOST_ICON_MAP = { standard: Rocket, premium: Star, elite: Crown };
const BOOST_STYLE_MAP = {
  standard: { color: "text-blue-600", bgColor: "bg-blue-50", borderColor: "border-blue-200" },
  premium: { color: "text-amber-600", bgColor: "bg-amber-50", borderColor: "border-amber-200" },
  elite: { color: "text-purple-600", bgColor: "bg-purple-50", borderColor: "border-purple-200" }
};

const STEP_LABELS = ['Details', 'Location & Price', 'Photos', 'Boost', 'Done'];

const ListingFormEmbed = ({ listingType = 'sale', accentColor = 'emerald', returnPath = '/sell/individual' }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedBoost, setSelectedBoost] = useState(null);
  const [enableBoost, setEnableBoost] = useState(false);
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState(null);
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [submittedListingId, setSubmittedListingId] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [completionScore, setCompletionScore] = useState(0);

  const imageInputRef = useRef(null);
  const floorPlanInputRef = useRef(null);
  const brochureInputRef = useRef(null);
  const [propertyImages, setPropertyImages] = useState([]);
  const [floorPlans, setFloorPlans] = useState([]);
  const [brochures, setBrochures] = useState([]);

  const { boostTiers: apiBoostTiers } = useAddonPricing();
  const boostTiers = Object.keys(DEFAULT_BOOST_TIERS).reduce((acc, key) => {
    acc[key] = { ...DEFAULT_BOOST_TIERS[key], ...(apiBoostTiers[key] || {}), icon: BOOST_ICON_MAP[key], ...BOOST_STYLE_MAP[key] };
    return acc;
  }, {});

  const [formData, setFormData] = useState({
    owner_name: '', email: '', phone: '',
    property_type: 'residential', property_subtype: '', title: '', description: '',
    address: '', locality: '', city: '', state: '', pincode: '',
    area_sqft: '', area_input_value: '', area_input_unit: 'sqft',
    bedrooms: '', bathrooms: '', parking: '',
    price: '', price_negotiable: true, listing_type: listingType,
    amenities: '', highlights: ''
  });

  const [quota, setQuota] = useState(null);
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      apiClient.get('/marketplace/listing-quota').then(r => setQuota(r.data)).catch(() => {});
    }
    // Track form started
    trackListingFormStarted(listingType);
    // Track abandonment on page leave
    const handleBeforeUnload = () => {
      if (step < 5) trackListingAbandoned(listingType, step);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const draft = getDraft('list_property');
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
      clearDraft();
      toast.info('Your draft listing has been restored.');
    }
  }, []);

  // Auto-save draft to localStorage on form change (debounced)
  useEffect(() => {
    if (step >= 5) return;
    const hasData = formData.owner_name || formData.title || formData.city;
    if (!hasData) return;
    const timer = setTimeout(() => {
      localStorage.setItem('zenstudios_embed_autosave', JSON.stringify({ formData, step, savedAt: new Date().toISOString() }));
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData, step]);

  const currentSubtypes = propertySubtypes[formData.property_type] || [];

  // Compute listing completion score (0-100)
  useEffect(() => {
    let score = 0;
    if (formData.title) score += 10;
    if (formData.description) score += 10;
    if (formData.price) score += 10;
    if (formData.city && formData.locality) score += 10;
    if (formData.address) score += 5;
    if (formData.pincode) score += 5;
    if (formData.amenities) score += 10;
    if (formData.bedrooms) score += 5;
    if (formData.bathrooms) score += 5;
    if (propertyImages.length >= 10) score += 15;
    else if (propertyImages.length >= 5) score += 10;
    else if (propertyImages.length > 0) score += 5;
    if (floorPlans.length > 0) score += 5;
    if (formData.property_subtype) score += 5;
    if (formData.owner_name && formData.email && formData.phone) score += 5;
    setCompletionScore(Math.min(score, 100));
  }, [formData, propertyImages, floorPlans]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => phone.replace(/\D/g, '').length >= 10;

  const validateFile = (file, type) => {
    const allowedTypes = type === 'image' ? ALLOWED_IMAGE_TYPES : ALLOWED_DOC_TYPES;
    const maxSize = type === 'image' ? MAX_IMAGE_SIZE : MAX_DOC_SIZE;
    if (!allowedTypes.includes(file.type)) { toast.error(`Invalid file type`); return false; }
    if (file.size > maxSize) { toast.error(`File too large`); return false; }
    return true;
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (propertyImages.length + files.length > MAX_IMAGES) { toast.error(`Max ${MAX_IMAGES} images`); return; }
    files.filter(f => validateFile(f, 'image')).forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setPropertyImages(prev => [...prev, { file, preview: ev.target.result, id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` }]);
      reader.readAsDataURL(file);
    });
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleFloorPlanUpload = (e) => {
    const files = Array.from(e.target.files);
    if (floorPlans.length + files.length > MAX_FLOOR_PLANS) { toast.error(`Max ${MAX_FLOOR_PLANS} floor plans`); return; }
    files.filter(f => validateFile(f, 'image') || validateFile(f, 'doc')).forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setFloorPlans(prev => [...prev, { file, preview: file.type.startsWith('image/') ? ev.target.result : null, name: file.name, id: `fp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` }]);
      reader.readAsDataURL(file);
    });
    if (floorPlanInputRef.current) floorPlanInputRef.current.value = '';
  };

  const handleBrochureUpload = (e) => {
    const files = Array.from(e.target.files);
    if (brochures.length + files.length > MAX_BROCHURES) { toast.error(`Max ${MAX_BROCHURES} brochures`); return; }
    files.filter(f => validateFile(f, 'doc')).forEach(file => {
      setBrochures(prev => [...prev, { file, name: file.name, id: `br_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` }]);
    });
    if (brochureInputRef.current) brochureInputRef.current.value = '';
  };

  const validateStep1 = () => {
    const e = {};
    if (!formData.owner_name.trim()) e.owner_name = 'Name is required';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!isValidEmail(formData.email)) e.email = 'Invalid email';
    if (!formData.phone.trim()) e.phone = 'Phone is required';
    else if (!isValidPhone(formData.phone)) e.phone = 'Invalid phone';
    if (!formData.property_subtype) e.property_subtype = 'Select subtype';
    if (!formData.title.trim()) e.title = 'Title is required';
    if (!formData.area_sqft || parseFloat(formData.area_sqft) <= 0) e.area_sqft = 'Enter valid area';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!formData.address.trim()) e.address = 'Address is required';
    if (!formData.locality.trim()) e.locality = 'Locality is required';
    if (!formData.city.trim()) e.city = 'City is required';
    if (!formData.state.trim()) e.state = 'State is required';
    if (!formData.pincode.trim()) e.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(formData.pincode)) e.pincode = 'Invalid pincode';
    if (!formData.price || parseFloat(formData.price) <= 0) e.price = 'Enter valid price';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) { trackListingStepCompleted(listingType, 1); setStep(2); }
    else if (step === 2 && validateStep2()) { trackListingStepCompleted(listingType, 2); setStep(3); }
    else if (step === 3) {
      if (propertyImages.length === 0) toast.info('Photos increase inquiries!');
      trackListingStepCompleted(listingType, 3);
      setStep(4);
    } else if (step < 3) toast.error('Please fill required fields');
  };

  const handleSubmit = async () => {
    if (!formData.owner_name || !formData.email || !formData.phone || !formData.title || !formData.property_subtype || !formData.city || !formData.price) {
      toast.error('Please complete all required fields'); return;
    }
    if (!captchaToken) {
      toast.error('Please complete the CAPTCHA verification'); return;
    }
    const token = getAuthToken();
    if (!token) {
      saveDraft('list_property', formData, returnPath);
      setPendingAction({ type: 'list_property', returnPath });
      toast.info('Please log in to submit. Your progress is saved.');
      navigate('/login', { state: { from: returnPath, pendingAction: 'list_property' } });
      return;
    }
    setLoading(true);
    try {
      const submitData = new FormData();
      Object.entries({
        owner_name: formData.owner_name, email: formData.email, phone: formData.phone,
        property_type: formData.property_type, property_subtype: formData.property_subtype,
        title: formData.title, description: formData.description || '',
        address: formData.address || '', locality: formData.locality || '',
        city: formData.city, state: formData.state || '', pincode: formData.pincode || '',
        area_sqft: formData.area_sqft || '0', area_input_value: formData.area_input_value || '',
        area_input_unit: formData.area_input_unit || 'sqft',
        bedrooms: formData.bedrooms || '', bathrooms: formData.bathrooms || '',
        price: formData.price || '0', listing_type: formData.listing_type,
        amenities: formData.amenities || '', enable_boost: enableBoost ? 'true' : 'false',
        captcha_token: captchaToken,
      }).forEach(([k, v]) => submitData.append(k, v));
      if (enableBoost && selectedBoost) submitData.append('boost_tier', selectedBoost);
      propertyImages.forEach(img => submitData.append('images', img.file));
      floorPlans.forEach(fp => submitData.append('floor_plans', fp.file));
      brochures.forEach(br => submitData.append('brochures', br.file));
      const response = await apiClient.post('/properties/list-request', submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
      trackListingSubmitted(listingType);
      localStorage.removeItem('zenstudios_embed_autosave');
      
      if (response.data.requires_otp) {
        setSubmittedListingId(response.data.request_id);
        setOtpRequired(true);
        setOtpSent(true);
        toast.success('Listing submitted! Please verify your phone number.');
        setStep(5);
      } else {
        toast.success(response.data.message || 'Listing submitted!');
        setStep(5);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'CAPTCHA_VALIDATION_FAILED') {
        toast.error('CAPTCHA verification failed. Please try again.');
        setCaptchaToken(null);
      } else {
        toast.error(detail || 'Submission failed. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setOtpLoading(true);
    try {
      const response = await apiClient.post('/otp/verify', {
        phone: formData.phone, otp_code: otpCode,
        purpose: 'listing_verification', listing_id: submittedListingId,
      });
      if (response.data.verified) {
        setOtpVerified(true);
        toast.success('Phone verified! Your listing is now live.');
      } else {
        toast.error(response.data.message || 'Invalid OTP');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally { setOtpLoading(false); }
  };

  const handleResendOtp = async () => {
    try {
      await apiClient.post('/otp/resend', { phone: formData.phone, purpose: 'listing_verification' });
      toast.success('OTP resent!');
    } catch (error) {
      toast.error('Failed to resend OTP');
    }
  };

  const accent = accentColor === 'blue' ? {
    bg: 'bg-blue-500', hover: 'hover:bg-blue-600', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-500', ring: 'ring-blue-500/20'
  } : {
    bg: 'bg-emerald-500', hover: 'hover:bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-500', ring: 'ring-emerald-500/20'
  };

  const FieldError = ({ field }) => errors[field] ? <p className="text-red-500 text-xs mt-1">{errors[field]}</p> : null;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden" data-testid="listing-form-embed">
      {/* Header */}
      <div className={`${accent.bg} px-6 py-4 text-white`}>
        <h3 className="text-lg font-bold" data-testid="embed-form-title">
          {listingType === 'rent' ? 'List Your Rental Property' : 'List Your Property for Sale'}
        </h3>
        <p className="text-sm opacity-80 mt-0.5">Free listing — goes live in minutes</p>
      </div>

      {/* Conversion Banner */}
      {step < 5 && (
        <div className={`mx-6 mt-4 rounded-lg ${accent.light} px-4 py-3 flex items-start gap-3`} data-testid="conversion-banner">
          <Rocket className={`w-5 h-5 ${accent.text} flex-shrink-0 mt-0.5`} />
          <div>
            <p className="font-semibold text-sm text-slate-900">Post your property in 30 seconds</p>
            <p className="text-xs text-slate-600 mt-0.5">Free listing for 15 days &middot; Reach thousands of {listingType === 'rent' ? 'tenants' : 'buyers'}</p>
          </div>
        </div>
      )}

      {/* Step Progress */}
      <div className="px-6 pt-5 pb-2">
        <div className="flex items-center justify-between mb-1">
          {STEP_LABELS.map((label, idx) => {
            const num = idx + 1;
            const isActive = step === num;
            const isDone = step > num;
            return (
              <React.Fragment key={num}>
                <div className="flex flex-col items-center" style={{ minWidth: 0, flex: '0 0 auto' }}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    isDone ? `${accent.bg} text-white` : isActive ? 'bg-slate-900 text-white ring-4 ' + accent.ring : 'bg-slate-100 text-slate-400'
                  }`} data-testid={`embed-step-${num}`}>
                    {isDone ? <CheckCircle className="w-4 h-4" /> : num}
                  </div>
                  <span className={`text-[10px] mt-1 whitespace-nowrap ${isActive ? 'text-slate-900 font-semibold' : isDone ? accent.text + ' font-medium' : 'text-slate-400'}`}>
                    {label}
                  </span>
                </div>
                {idx < 4 && <div className={`flex-1 h-0.5 mx-1 rounded ${isDone ? accent.bg : 'bg-slate-200'}`} />}
              </React.Fragment>
            );
          })}
        </div>
        {step < 5 && (
          <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-400">
            <Save className="w-3 h-3" />
            <span data-testid="autosave-msg">Your progress is automatically saved</span>
          </div>
        )}
      </div>

      {/* Quota Banner */}
      {quota && step < 5 && (
        <div className={`mx-6 mt-2 rounded-lg border px-3 py-2 flex items-center justify-between text-sm ${
          !quota.can_create ? 'bg-red-50 border-red-200' : accent.light + ' ' + accent.border
        }`} data-testid="embed-quota-banner">
          <div className="flex items-center gap-2">
            <span className="font-bold text-xs">{quota.active_count}/{quota.max_active}</span>
            <span className="text-xs">{quota.plan_name === 'free_user' ? 'Free' : 'Seller Basic'} &middot; {quota.listing_validity_days}d</span>
          </div>
          {!quota.can_create && <span className="text-xs text-red-600 font-medium">Limit reached</span>}
        </div>
      )}

      {/* Form Content */}
      <div className="p-6">
        {/* Step 1: Property Details */}
        {step === 1 && (
          <div className="space-y-4" data-testid="embed-step-1-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Your Name *</Label>
                <Input placeholder="Full Name" value={formData.owner_name} onChange={e => updateField('owner_name', e.target.value)} className={`h-9 text-sm ${errors.owner_name ? 'border-red-500' : ''}`} />
                <FieldError field="owner_name" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" placeholder="email@example.com" value={formData.email} onChange={e => updateField('email', e.target.value)} className={`h-9 text-sm ${errors.email ? 'border-red-500' : ''}`} />
                <FieldError field="email" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Phone *</Label>
              <Input placeholder="+91 XXXXX XXXXX" value={formData.phone} onChange={e => updateField('phone', e.target.value)} className={`h-9 text-sm ${errors.phone ? 'border-red-500' : ''}`} />
              <FieldError field="phone" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Property Type *</Label>
                <Select value={formData.property_type} onValueChange={v => updateField('property_type', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="agricultural">Agricultural</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Subtype *</Label>
                <Select value={formData.property_subtype} onValueChange={v => updateField('property_subtype', v)}>
                  <SelectTrigger className={`h-9 text-sm ${errors.property_subtype ? 'border-red-500' : ''}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {currentSubtypes.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FieldError field="property_subtype" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Property Title *</Label>
              <Input placeholder="e.g., Luxurious 3BHK in Jubilee Hills" value={formData.title} onChange={e => updateField('title', e.target.value)} className={`h-9 text-sm ${errors.title ? 'border-red-500' : ''}`} />
              <FieldError field="title" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea placeholder="Key features, USPs, landmarks..." rows={3} value={formData.description} onChange={e => updateField('description', e.target.value)} className="text-sm" />
            </div>
            <div>
              <AreaInput value={formData.area_input_value} unit={formData.area_input_unit}
                onChange={({ value, unit, sqftValue }) => { updateField('area_input_value', value); updateField('area_input_unit', unit); updateField('area_sqft', sqftValue || ''); }}
                propertyType={formData.property_subtype} showPreview={true} required={true} error={errors.area_sqft}
              />
            </div>
            {formData.property_type === 'residential' && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Bedrooms</Label>
                  <Select value={formData.bedrooms} onValueChange={v => updateField('bedrooms', v)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="BHK" /></SelectTrigger>
                    <SelectContent>{[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} BHK</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Bathrooms</Label>
                  <Select value={formData.bathrooms} onValueChange={v => updateField('bathrooms', v)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Bath" /></SelectTrigger>
                    <SelectContent>{[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Parking</Label>
                  <Select value={formData.parking} onValueChange={v => updateField('parking', v)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Slots" /></SelectTrigger>
                    <SelectContent>{[0,1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <Button onClick={handleNextStep} className={`w-full ${accent.bg} ${accent.hover} text-white`} data-testid="embed-next-step-1">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Location & Pricing */}
        {step === 2 && (
          <div className="space-y-4" data-testid="embed-step-2-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Address *</Label>
                <Input placeholder="Building/House No., Street" value={formData.address} onChange={e => updateField('address', e.target.value)} className={`h-9 text-sm ${errors.address ? 'border-red-500' : ''}`} />
                <FieldError field="address" />
              </div>
              <div>
                <Label className="text-xs">Locality *</Label>
                <Input placeholder="e.g., Jubilee Hills" value={formData.locality} onChange={e => updateField('locality', e.target.value)} className={`h-9 text-sm ${errors.locality ? 'border-red-500' : ''}`} />
                <FieldError field="locality" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">City *</Label>
                <LocationSearch placeholder="City..." value={formData.city} onChange={val => updateField('city', val)} types="city,town"
                  className={`mt-0.5 ${errors.city ? '[&_input]:border-red-500' : ''}`}
                  onLocationSelect={loc => { if (loc?.state) updateField('state', loc.state); if (loc?.pincode) updateField('pincode', loc.pincode); }}
                />
                <FieldError field="city" />
              </div>
              <div>
                <Label className="text-xs">State *</Label>
                <LocationSearch placeholder="State..." value={formData.state} onChange={val => updateField('state', val)} types="state"
                  className={`mt-0.5 ${errors.state ? '[&_input]:border-red-500' : ''}`}
                />
                <FieldError field="state" />
              </div>
              <div>
                <Label className="text-xs">Pincode *</Label>
                <Input placeholder="500033" value={formData.pincode} onChange={e => updateField('pincode', e.target.value)} className={`h-9 text-sm ${errors.pincode ? 'border-red-500' : ''}`} />
                <FieldError field="pincode" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Price (₹) *</Label>
                <Input type="number" placeholder={listingType === 'rent' ? 'Monthly Rent' : 'Total Price'} value={formData.price} onChange={e => updateField('price', e.target.value)} className={`h-9 text-sm ${errors.price ? 'border-red-500' : ''}`} />
                <FieldError field="price" />
              </div>
              <div className="flex items-end pb-1">
                <div className="flex items-center gap-2">
                  <Checkbox id="embed-negotiable" checked={formData.price_negotiable} onCheckedChange={v => updateField('price_negotiable', v)} />
                  <Label htmlFor="embed-negotiable" className="text-xs cursor-pointer">Negotiable</Label>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Amenities (comma separated)</Label>
              <Input placeholder="Pool, Gym, Club House..." value={formData.amenities} onChange={e => updateField('amenities', e.target.value)} className="h-9 text-sm" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-10">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={handleNextStep} className={`flex-1 h-10 ${accent.bg} ${accent.hover} text-white`} data-testid="embed-next-step-2">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Photos & Media */}
        {step === 3 && (
          <div className="space-y-5" data-testid="embed-step-3-form">
            {/* Images */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-xs font-semibold flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Photos ({propertyImages.length}/{MAX_IMAGES})</Label>
              </div>
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-5 text-center hover:border-slate-400 transition-colors cursor-pointer bg-slate-50/50"
                onClick={() => imageInputRef.current?.click()} data-testid="embed-image-upload">
                <Upload className="w-7 h-7 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">Click to upload photos</p>
                <p className="text-xs text-slate-400">JPG, PNG, WebP — Max 5MB each</p>
                <input ref={imageInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple className="hidden" onChange={handleImageUpload} />
              </div>
              {propertyImages.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {propertyImages.map((img, idx) => (
                    <div key={img.id} className="relative group aspect-square">
                      <img src={img.preview} alt={`Photo ${idx+1}`} className="w-full h-full object-cover rounded-lg border" />
                      {idx === 0 && <span className={`absolute bottom-0.5 left-0.5 ${accent.bg} text-white text-[9px] px-1.5 py-0.5 rounded`}>Cover</span>}
                      <button type="button" onClick={() => setPropertyImages(prev => prev.filter(i => i.id !== img.id))}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Floor plans */}
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1 mb-2"><FileText className="w-3.5 h-3.5 text-blue-600" /> Floor Plans ({floorPlans.length}/{MAX_FLOOR_PLANS}) — Optional</Label>
              <div className="border-2 border-dashed border-blue-200 rounded-lg p-3 text-center cursor-pointer bg-blue-50/30 hover:border-blue-400 transition-colors"
                onClick={() => floorPlanInputRef.current?.click()}>
                <p className="text-xs text-slate-600">Upload floor plans (images or PDF)</p>
                <input ref={floorPlanInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf" multiple className="hidden" onChange={handleFloorPlanUpload} />
              </div>
              {floorPlans.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {floorPlans.map(fp => (
                    <div key={fp.id} className="relative group w-16 h-16">
                      {fp.preview ? <img src={fp.preview} alt={fp.name} className="w-full h-full object-cover rounded border" />
                        : <div className="w-full h-full bg-blue-50 border border-blue-200 rounded flex items-center justify-center"><FileText className="w-5 h-5 text-blue-500" /></div>}
                      <button type="button" onClick={() => setFloorPlans(prev => prev.filter(f => f.id !== fp.id))}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-2.5 h-2.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Brochures */}
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1 mb-2"><FileText className="w-3.5 h-3.5 text-amber-600" /> Brochures ({brochures.length}/{MAX_BROCHURES}) — Optional</Label>
              <div className="border-2 border-dashed border-amber-200 rounded-lg p-3 text-center cursor-pointer bg-amber-50/30 hover:border-amber-400 transition-colors"
                onClick={() => brochureInputRef.current?.click()}>
                <p className="text-xs text-slate-600">Upload brochures (PDF)</p>
                <input ref={brochureInputRef} type="file" accept="application/pdf" multiple className="hidden" onChange={handleBrochureUpload} />
              </div>
              {brochures.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {brochures.map(br => (
                    <div key={br.id} className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs">
                      <FileText className="w-3 h-3 text-amber-600" />
                      <span className="max-w-[80px] truncate">{br.name}</span>
                      <button type="button" onClick={() => setBrochures(prev => prev.filter(b => b.id !== br.id))} className="text-red-500"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1 h-10"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={handleNextStep} className={`flex-1 h-10 ${accent.bg} ${accent.hover} text-white`} data-testid="embed-next-step-3">
                {propertyImages.length === 0 ? 'Skip' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Boost */}
        {step === 4 && (
          <div className="space-y-4" data-testid="embed-step-4-form">
            <div className="flex items-center gap-3 p-3 border rounded-xl">
              <Checkbox id="embed-boost" checked={enableBoost} onCheckedChange={setEnableBoost} />
              <Label htmlFor="embed-boost" className="cursor-pointer flex-1">
                <span className="font-medium text-sm">Enable Boost</span>
                <span className="text-xs text-slate-500 block">Get more inquiries and sell/rent faster</span>
              </Label>
            </div>
            {enableBoost && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="flex items-start gap-2 text-xs"><Eye className="w-4 h-4 text-slate-500 flex-shrink-0" /><span>Higher visibility</span></div>
                  <div className="flex items-start gap-2 text-xs"><Target className="w-4 h-4 text-slate-500 flex-shrink-0" /><span>Targeted reach</span></div>
                  <div className="flex items-start gap-2 text-xs"><TrendingUp className="w-4 h-4 text-slate-500 flex-shrink-0" /><span>Faster results</span></div>
                </div>
                {Object.entries(boostTiers).map(([key, tier]) => (
                  <div key={key} onClick={() => setSelectedBoost(key)}
                    className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedBoost === key ? `${tier.borderColor} ${tier.bgColor} shadow-md` : 'border-slate-200 hover:border-slate-300'
                    }`} data-testid={`embed-boost-${key}`}>
                    {tier.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full">Popular</span>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <tier.icon className={`w-5 h-5 ${tier.color}`} />
                        <div>
                          <p className="font-semibold text-sm">{tier.name}</p>
                          <p className="text-xs text-slate-500">{tier.duration} &middot; {tier.multiplier} visibility</p>
                        </div>
                      </div>
                      <p className="font-bold">₹{tier.price.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* CAPTCHA Widget */}
            <TurnstileWidget
              onVerify={setCaptchaToken}
              onExpire={() => setCaptchaToken(null)}
              className="flex justify-center"
            />
            {/* Completion Score */}
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-slate-700">Listing Completeness</span>
                <span className={`text-xs font-bold ${completionScore >= 80 ? 'text-green-600' : completionScore >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{completionScore}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full transition-all ${completionScore >= 80 ? 'bg-green-500' : completionScore >= 50 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${completionScore}%` }} />
              </div>
              {completionScore < 80 && (
                <div className="mt-2 space-y-1">
                  {propertyImages.length < 10 && <p className="text-[10px] text-amber-600">Listings with 10+ photos receive 3x more enquiries</p>}
                  {!formData.amenities && <p className="text-[10px] text-amber-600">Add amenities to improve ranking</p>}
                  {!formData.description && <p className="text-[10px] text-amber-600">Add a description to attract more buyers</p>}
                  {!formData.locality && <p className="text-[10px] text-amber-600">Add exact location to appear in map search</p>}
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(3)} className="flex-1 h-10"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={handleSubmit} disabled={loading || !captchaToken} className={`flex-1 h-10 ${accent.bg} ${accent.hover} text-white`} data-testid="embed-submit-btn">
                {loading ? 'Submitting...' : enableBoost && selectedBoost ? `Submit & Pay ₹${boostTiers[selectedBoost]?.price.toLocaleString()}` : 'Submit Listing'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Success + OTP Verification */}
        {step === 5 && (
          <div className="text-center py-6" data-testid="embed-step-5-success">
            {otpRequired && !otpVerified ? (
              <div data-testid="otp-verification-section">
                <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Verify Your Phone</h3>
                <p className="text-sm text-slate-600 mb-4">We've sent a 6-digit OTP to <strong>{formData.phone}</strong></p>
                <div className="max-w-xs mx-auto space-y-3">
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="Enter 6-digit OTP"
                    value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-lg tracking-widest h-12"
                    data-testid="otp-input"
                  />
                  <Button onClick={handleVerifyOtp} disabled={otpLoading || otpCode.length !== 6} className={`w-full h-10 ${accent.bg} ${accent.hover} text-white`} data-testid="otp-verify-btn">
                    {otpLoading ? 'Verifying...' : 'Verify & Go Live'}
                  </Button>
                  <button type="button" onClick={handleResendOtp} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="otp-resend-btn">
                    Didn't receive OTP? Resend
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={`w-16 h-16 ${accent.light} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <CheckCircle className={`w-8 h-8 ${accent.text}`} />
                </div>
                <h3 className="text-xl font-bold mb-2">{otpVerified ? 'Listing is Live!' : 'Listing Submitted!'}</h3>
                <p className="text-sm text-slate-600 mb-6 max-w-xs mx-auto">
                  {otpVerified
                    ? 'Your listing is now visible to thousands of potential buyers and tenants.'
                    : 'Our team will review and publish your listing within 24 hours. You\'ll receive a confirmation email.'}
                </p>
                {enableBoost && selectedBoost && (
                  <div className={`${accent.light} rounded-lg p-3 mb-4 text-sm`}>
                    <p className="font-medium">{boostTiers[selectedBoost]?.name} Activated</p>
                    <p className="text-xs text-slate-600">{boostTiers[selectedBoost]?.multiplier} visibility for {boostTiers[selectedBoost]?.duration}</p>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate('/real-estate')} className="flex-1 h-10 text-sm">Browse Properties</Button>
                  <Button onClick={() => navigate('/dashboard')} className={`flex-1 h-10 text-sm ${accent.bg} ${accent.hover} text-white`}>Dashboard</Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingFormEmbed;
